/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useContext, createContext } from 'react';
import PropTypes from 'prop-types';

import { FormSelect, FormInput, FormDate, Button } from '@spotahome/ui-library';

import { trans } from '@spotahome/soyuz/client';

import * as yup from 'yup';

import FormError from '../FormError';

import './FormProvider.scss';

const FormContext = createContext('FormContext');

const FormProvider = ({
  onSubmit,
  onError,
  schema,
  fieldsClassName,
  children,
  disabled
}) => {
  const [values, setValues] = useState({});
  const [errors, setFormErrors] = useState({});

  const validateField = async ({ name, value }) => {
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleInputChange = async ({ value, name }) => {
    setValues(prevState => ({
      ...prevState,
      [name]: value
    }));

    // if the field has an error, check if new value removes it
    if (errors[name]) {
      await validateField({ name, value });
    }
  };

  const handleInputBlur = async ({ value, name }) => {
    await validateField({ name, value });
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (disabled) {
      return;
    }

    try {
      await schema.validate(values, { abortEarly: false });
      onSubmit(values);
    } catch (error) {
      const newErrors = {};
      if (error && error.inner) {
        error.inner.forEach(errorItem => {
          newErrors[errorItem.path] = errorItem.message;
        });
        setFormErrors(newErrors);
      }
      onError();
    }
  };

  const value = {
    errors,
    values,
    disabled,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
    fieldsClassName
  };

  return (
    <FormContext.Provider value={value}>
      <form onSubmit={handleSubmit}>{children}</form>
    </FormContext.Provider>
  );
};

FormProvider.Input = ({ name, title, placeholder, type, rows }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName
  } = useContext(FormContext);
  return (
    <div className={fieldsClassName}>
      <FormInput
        title={title}
        name={name}
        type={type}
        rows={rows}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Input.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  type: PropTypes.oneOf(['text', 'textarea', 'password', 'email', 'number']),
  placeholder: PropTypes.string,
  rows: PropTypes.number
};

FormProvider.Input.defaultProps = {
  title: '',
  placeholder: '',
  type: 'input',
  rows: 1
};

FormProvider.Date = ({ name, title, placeholder }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);
  return (
    <div className={fieldsClassName}>
      <FormDate
        title={title}
        name={name}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Date.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string
};

FormProvider.Date.defaultProps = {
  title: '',
  placeholder: ''
};

FormProvider.Submit = ({ children }) => {
  const { fieldsClassName, disabled } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <Button
        disabled={disabled}
        size="big-redesign"
        color="primary"
        data-test="form-submit"
        type="submit"
      >
        {children}
      </Button>
    </div>
  );
};

FormProvider.Submit.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

FormProvider.Select = ({ name, title, placeholder, options }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <FormSelect
        name={name}
        options={options}
        title={title}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Select.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      text: PropTypes.string
    })
  ).isRequired
};

FormProvider.Select.defaultProps = {
  title: '',
  placeholder: ''
};

FormProvider.propTypes = {
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  schema: PropTypes.shape({
    validate: PropTypes.func
  }).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  fieldsClassName: PropTypes.string,
  disabled: PropTypes.bool
};

FormProvider.defaultProps = {
  onSubmit: () => {},
  onError: () => {},
  fieldsClassName: 'form-provider__field',
  disabled: false
};

export default FormProvider;
